import { useCallback, useState } from 'react';
import { PlaybookTransformItem, PlaybookMapping, PlaybookTransformType, PlaybookTransformContent, PlaybookEnrichment } from '@/types/playbook';
import { UnifiedSchema } from '@/types/schema';
import { autoMapPlaybookProperties } from '@/services/playbookAIMappingService';
import { findParameterDataTypeInSchema } from '@/utils/schemaUtils';
import { EditorSelection } from '@/components/playbooks/PlaybookEditorTab';
import { createAutoMappingSummary } from '@/services/mappingConflictService';
import { MappingSummaryData } from '@/components/dialogs/MappingSummaryDialog';

interface UsePlaybookAutoMappingProps {
  inputSchema: UnifiedSchema | null;
  outputSchema: UnifiedSchema | null;
  transforms: PlaybookTransformItem[];
  onAddTransforms: (transforms: PlaybookTransformItem[]) => void;
  onUpdateTransform: (transformId: string, updates: Partial<PlaybookTransformItem>) => void;
  selection: EditorSelection;
  allUiTransforms: PlaybookTransformItem[];
  allowUndefinedTransforms: boolean;
  baselineSchema?: 'input' | 'output';
  isPropertyPairApplicableToAllEvents: (inputPath: string | undefined, outputPath: string | undefined) => boolean;
  allEvents: Array<{ id: string }>;
  uiEventConfigs?: Array<{ id: string; name: string; codeName: string }>;
}

export const usePlaybookAutoMapping = ({
  inputSchema,
  outputSchema,
  transforms,
  onAddTransforms,
  onUpdateTransform,
  selection,
  allUiTransforms,
  baselineSchema,
  isPropertyPairApplicableToAllEvents,
  allEvents,
}: UsePlaybookAutoMappingProps) => {
  // State for mapping summary dialog
  const [showMappingSummary, setShowMappingSummary] = useState(false);
  const [mappingSummaryData, setMappingSummaryData] = useState<MappingSummaryData | null>(null);
  const [pendingTransforms, setPendingTransforms] = useState<PlaybookTransformItem[]>([]);

  // Helper function to map schema event IDs to UI event IDs
  const mapSchemaEventIdsToUiEventIds = useCallback((schemaEventIds: string[], sourceSchema: UnifiedSchema | null): string[] => {
    if (!sourceSchema || !schemaEventIds || schemaEventIds.length === 0) {
      return [];
    }

    const uiEventIds: string[] = [];
    
    // For each schema event ID, find the corresponding UI event ID
    schemaEventIds.forEach(schemaEventId => {
      // Find the schema event by ID
      const schemaEvent = sourceSchema.events?.find(e => e.id === schemaEventId);
      if (schemaEvent) {
        // Find the corresponding UI event by matching name or codeName
        // UI events have IDs like 'event_event1_1751277380349' but names like 'Event1'
        const uiEvent = allEvents.find(uiEvent => {
          // Try to match by extracting the event name from the UI event ID
          // UI event IDs follow pattern: event_{eventName}_{timestamp}
          const uiEventIdParts = uiEvent.id.split('_');
          if (uiEventIdParts.length >= 2) {
            const extractedName = uiEventIdParts[1]; // e.g., 'event1' from 'event_event1_1751277380349'
            
            // Multiple matching strategies:
            // 1. Direct name match (case-insensitive)
            if (schemaEvent.name.toLowerCase() === extractedName.toLowerCase() ||
                schemaEvent.codeName?.toLowerCase() === extractedName.toLowerCase()) {
              return true;
            }
            
            // 2. Partial match - check if schema event name contains the UI event name or vice versa
            const schemaNameLower = schemaEvent.name.toLowerCase();
            const extractedNameLower = extractedName.toLowerCase();
            
            if (schemaNameLower.includes(extractedNameLower) || extractedNameLower.includes(schemaNameLower)) {
              return true;
            }
            
            // 3. Remove common words and try matching
            const cleanSchemaName = schemaNameLower.replace(/\b(event|basic|capture)\b/g, '').trim();
            const cleanExtractedName = extractedNameLower.replace(/\b(event|basic|capture)\b/g, '').trim();
            
            if (cleanSchemaName && cleanExtractedName && 
                (cleanSchemaName.includes(cleanExtractedName) || cleanExtractedName.includes(cleanSchemaName))) {
              return true;
            }
          }
          return false;
        });
        
        if (uiEvent) {
          uiEventIds.push(uiEvent.id);
        } else {
          console.warn(`mapSchemaEventIdsToUiEventIds: Could not find UI event for schema event "${schemaEvent.name}" (${schemaEventId})`);
        }
      }
    });

    return uiEventIds;
  }, [allEvents]);

  // Helper function to find event IDs for a given parameter path in a schema
  const findEventIdsForParameterPath = useCallback((schema: UnifiedSchema | null, paramPath: string): string[] | undefined => {
    if (!schema || !schema.events || !paramPath) {
      return undefined;
    }

    const schemaEventIds: string[] = [];
    for (const event of schema.events) {
      if (event.parameterFullPaths && event.parameterFullPaths.includes(paramPath)) {
        schemaEventIds.push(event.id);
      }
    }
    
    if (schemaEventIds.length === 0) {
      return undefined;
    }

    // Map schema event IDs to UI event IDs
    const uiEventIds = mapSchemaEventIdsToUiEventIds(schemaEventIds, schema);
    return uiEventIds.length > 0 ? uiEventIds : undefined;
  }, [mapSchemaEventIdsToUiEventIds]);

  // Helper function to create transform with proper typing
  const createTransform = useCallback((
    inputPath: string,
    outputPath: string,
    mappingType: 'direct' | 'no-match',
    index: number,
    transformType: PlaybookTransformType,
    currentBaselineSchema: 'input' | 'output'
  ): PlaybookTransformItem => {
    let appliesToScope: PlaybookTransformItem['appliesToScope'];
    let eventIds: string[] | undefined;

    if (isPropertyPairApplicableToAllEvents(inputPath, outputPath)) {
      appliesToScope = 'global';
      eventIds = undefined;
    } else {
      appliesToScope = 'eventSpecific';
      let targetSchemaForEventLookup: UnifiedSchema | null = null;
      let pathToLookup = '';

      if (currentBaselineSchema === 'input') {
        targetSchemaForEventLookup = inputSchema;
        pathToLookup = inputPath;
      } else if (currentBaselineSchema === 'output') {
        targetSchemaForEventLookup = outputSchema;
        pathToLookup = outputPath;
      }

      if (targetSchemaForEventLookup && pathToLookup) {
        eventIds = findEventIdsForParameterPath(targetSchemaForEventLookup, pathToLookup);
      } else {
        // Fallback: if schema or path is missing, or if it's not global and no specific event found
        // This should ideally not happen if auto-mapping is triggered in a valid context.
        // For now, default to the currently selected event if available, or undefined.
        const eventId = selection.type === 'event' && selection.eventId ? selection.eventId : undefined;
        eventIds = eventId ? [eventId] : undefined;
      }
    }

    const inputType = inputPath && inputSchema ? findParameterDataTypeInSchema(inputSchema, inputPath) : undefined;
    const outputType = outputPath && outputSchema ? findParameterDataTypeInSchema(outputSchema, outputPath) : undefined;

    const uniqueId = `transform_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`;

    let content: PlaybookTransformContent;
    if (transformType === 'enrichment') {
      content = {
        type: 'DirectValue',
        outputKey: outputPath,
        value: '',
      } as PlaybookEnrichment;
    } else {
      // For mapping transforms, always include DirectAssign transform for proper display
      content = {
        inputKey: inputPath,
        outputKey: outputPath,
        transforms: [{ type: 'DirectAssign' }], // Always include DirectAssign for auto-mapped transforms
      } as PlaybookMapping;
    }

    return {
      id: uniqueId,
      type: transformType,
      content: content,
      appliesToScope,
      eventIds,
      mappingType,
      inputPath,
      outputPath,
      inputType,
      outputType,
      isNew: true,
    };
  }, [inputSchema, outputSchema, findEventIdsForParameterPath, isPropertyPairApplicableToAllEvents, selection]);

  // Helper function to check for existing shared transforms
  const findExistingSharedTransform = useCallback((
    inputPath: string,
    outputPath: string
  ) => {
    if (selection.type !== 'event' || !selection.eventId) return null;

    return allUiTransforms.find(t =>
      t.type === 'mapping' &&
      (t.content as PlaybookMapping)?.inputKey === inputPath &&
      (t.content as PlaybookMapping)?.outputKey === outputPath &&
      t.appliesToScope === 'eventSpecific' &&
      t.eventIds && t.eventIds.length > 0 &&
      !t.eventIds.includes(selection.eventId!)
    );
  }, [allUiTransforms, selection]);

  // Helper function to update existing shared transform
  const updateExistingSharedTransform = useCallback((
    existingTransform: PlaybookTransformItem,
    logMessage: string
  ) => {
    if (selection.eventId) {
      onUpdateTransform(existingTransform.id, {
        eventIds: [...(existingTransform.eventIds || []), selection.eventId],
      });
      console.log(logMessage);
    }
  }, [onUpdateTransform, selection.eventId]);

  // Process input baseline mapping
  const processInputBaselineMapping = useCallback((currentBaselineSchema: 'input' | 'output') => {
    if (!inputSchema || !outputSchema) return { newTransforms: [], mappedCount: 0, unmappedCount: 0 };

    const existingInputPaths = transforms
      .filter(t => t.type === 'mapping')
      .map(t => (t.content as PlaybookMapping).inputKey)
      .filter(Boolean) as string[];

    const unmappedInputProperties = inputSchema.parameters.filter(
      param => !existingInputPaths.includes(param.path)
    );

    if (unmappedInputProperties.length === 0) {
      return { newTransforms: [], mappedCount: 0, unmappedCount: 0 };
    }

    const mappingResults = autoMapPlaybookProperties(unmappedInputProperties, outputSchema.parameters);
    const newTransforms: PlaybookTransformItem[] = [];
    let mappedCount = 0;
    let unmappedCount = 0;

    unmappedInputProperties.forEach((inputParam, index) => {
      const mappingResult = mappingResults.find(r => r.inputPath === inputParam.path);
      
      if (mappingResult && mappingResult.matchStatus === 'matched') {
        // Check for existing shared transform
        const existingSharedTransform = findExistingSharedTransform(inputParam.path, mappingResult.normalizedPath);
        
        if (existingSharedTransform) {
          updateExistingSharedTransform(
            existingSharedTransform,
            `Updated existing shared mapping for ${inputParam.path} -> ${mappingResult.normalizedPath} to include event ${selection.eventId}`
          );
          mappedCount++;
          return;
        }

        const newTransform = createTransform(inputParam.path, mappingResult.normalizedPath || '', 'direct', index, 'mapping', currentBaselineSchema);
        newTransforms.push(newTransform);
        mappedCount++;
      } else {
        // Check for existing shared transform for unmatched properties
        const existingSharedTransform = findExistingSharedTransform(inputParam.path, '');
        
        if (existingSharedTransform) {
          updateExistingSharedTransform(
            existingSharedTransform,
            `Updated existing shared unmatched mapping for ${inputParam.path} to include event ${selection.eventId}`
          );
          unmappedCount++;
          return;
        }

        const newTransform = createTransform(inputParam.path, '', 'no-match', index, 'mapping', currentBaselineSchema);
        newTransforms.push(newTransform);
        unmappedCount++;
      }
    });
    return { newTransforms, mappedCount, unmappedCount };
  }, [inputSchema, outputSchema, transforms, findExistingSharedTransform, updateExistingSharedTransform, createTransform, selection.eventId]);

  // Process output baseline mapping
  const processOutputBaselineMapping = useCallback((currentBaselineSchema: 'input' | 'output') => {
    if (!inputSchema || !outputSchema) return { newTransforms: [], mappedCount: 0, unmappedCount: 0 };

    const existingOutputPaths = transforms
      .filter(t => t.type === 'mapping' || t.type === 'enrichment')
      .map(t => {
        if (t.type === 'mapping') {
          return (t.content as PlaybookMapping).outputKey;
        } else if (t.type === 'enrichment') {
          return (t.content as PlaybookEnrichment).outputKey;
        }
        return null;
      })
      .filter(Boolean) as string[];

    const unmappedOutputProperties = outputSchema.parameters.filter(
      param => !existingOutputPaths.includes(param.path)
    );

    if (unmappedOutputProperties.length === 0) {
      return { newTransforms: [], mappedCount: 0, unmappedCount: 0 };
    }

    const reverseMapping = autoMapPlaybookProperties(unmappedOutputProperties, inputSchema.parameters);
    const newTransforms: PlaybookTransformItem[] = [];
    let mappedCount = 0;
    let unmappedCount = 0;

    unmappedOutputProperties.forEach((outputParam, index) => {
      const mappingResult = reverseMapping.find(r => r.inputPath === outputParam.path);
      
      if (mappingResult && mappingResult.matchStatus === 'matched') {
        // Check for existing shared transform
        const existingSharedTransform = findExistingSharedTransform(mappingResult.normalizedPath || '', outputParam.path);
        
        if (existingSharedTransform) {
          updateExistingSharedTransform(
            existingSharedTransform,
            `Updated existing shared mapping for ${mappingResult.normalizedPath} -> ${outputParam.path} to include event ${selection.eventId}`
          );
          mappedCount++;
          return;
        }

        const newTransform = createTransform(mappingResult.normalizedPath || '', outputParam.path, 'direct', index, 'mapping', currentBaselineSchema);
        newTransforms.push(newTransform);
        mappedCount++;
      } else {
        // For unmatched output properties, create mapping transforms with DirectAssign
        // This ensures 'DirectAssign' is always present for 'no-match' scenarios as requested.
        const newTransform = createTransform('', outputParam.path, 'no-match', index, 'mapping', currentBaselineSchema); // Changed to 'mapping'
        newTransforms.push(newTransform);
        unmappedCount++;
      }
    });

    return { newTransforms, mappedCount, unmappedCount };
  }, [inputSchema, outputSchema, transforms, findExistingSharedTransform, updateExistingSharedTransform, createTransform, selection.eventId]);

  // Handle conflict resolution from summary dialog
  const handleConflictResolution = useCallback((resolvedConflicts: { [outputPath: string]: string }) => {
    if (!mappingSummaryData || pendingTransforms.length === 0) return;

    // Filter out conflicting transforms and keep only resolved ones
    const finalTransforms = pendingTransforms.filter(transform => {
      const outputPath = transform.outputPath;
      if (!outputPath) return true; // Keep transforms without output paths
      
      // Check if this transform's output conflicts with others
      const conflict = mappingSummaryData.conflicts.find(c => c.outputPath === outputPath);
      if (!conflict) return true; // No conflict, keep it
      
      // Keep only if this transform's input matches the resolved choice
      const chosenInputPath = resolvedConflicts[outputPath];
      return transform.inputPath === chosenInputPath;
    });

    // Add the resolved transforms
    if (finalTransforms.length > 0) {
      onAddTransforms(finalTransforms);
    }

    // Clean up state
    setShowMappingSummary(false);
    setMappingSummaryData(null);
    setPendingTransforms([]);
  }, [mappingSummaryData, pendingTransforms, onAddTransforms]);

  // Enhanced Auto-map functionality with conflict detection
  const handleAutoMap = useCallback(() => {
    if (!inputSchema || !outputSchema) {
      console.warn('Cannot auto-map: input or output schema is missing');
      return;
    }

    const effectiveBaselineSchema = baselineSchema || 'input';
    let result;

    if (effectiveBaselineSchema === 'input') {
      result = processInputBaselineMapping(effectiveBaselineSchema);
    } else if (effectiveBaselineSchema === 'output') {
      result = processOutputBaselineMapping(effectiveBaselineSchema);
    } else {
      result = processInputBaselineMapping(effectiveBaselineSchema);
    }

    const { newTransforms } = result;

    if (newTransforms.length === 0) {
      console.log('No new transforms to add from auto mapping');
      return;
    }

    // Convert transforms to mapping results for conflict detection
    const mappingResults = newTransforms.map(transform => ({
      inputPath: transform.inputPath || '',
      normalizedPath: transform.outputPath || '',
      certaintyScore: 1.0, // Auto mapping has perfect certainty
      matchStatus: (transform.outputPath ? 'matched' : 'no-match') as 'matched' | 'no-match'
    }));

    // Check for conflicts
    const summaryData = createAutoMappingSummary(mappingResults, inputSchema, outputSchema, transforms, effectiveBaselineSchema);

    if (summaryData.conflicts.length > 0) {
      // Show summary dialog for conflict resolution
      setPendingTransforms(newTransforms);
      setMappingSummaryData(summaryData);
      setShowMappingSummary(true);
    } else {
      // No conflicts, add transforms directly
      onAddTransforms(newTransforms);
      
      // Show summary dialog for successful mapping
      setMappingSummaryData(summaryData);
      setShowMappingSummary(true);
    }
  }, [inputSchema, outputSchema, baselineSchema, processInputBaselineMapping, processOutputBaselineMapping, onAddTransforms, transforms]);

  return {
    handleAutoMap,
    showMappingSummary,
    mappingSummaryData,
    handleConflictResolution,
    setShowMappingSummary
  };
};
